import { Ban, CheckCircle2, History, ScrollText, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpmpItemModal } from '../components/PpmpItemModal';
import { ppmpItemsApi } from '../procurementCatalogsApi';
import type { PpmpItemCatalogEntry } from '../procurementCatalogTypes';

type TabKey = 'info';

/**
 * One entry in the catalog a procurement line's description is drawn from.
 *
 * On the **header** layout: a catalog row reads as one continuous thing. It earns its keep on
 * the SPECIFICATION — the list can only show one line of plain text, and the whole document is
 * what a reader opens the row to read.
 */
export function PpmpItemProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [entry, setEntry] = useState<PpmpItemCatalogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      ppmpItemsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setEntry(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this catalog item.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Procurement Planning',
    { label: 'Settings', to: '/procurement/procurement-planning/settings' },
    { label: 'PPMP Items', to: '/procurement/procurement-planning/settings/ppmp-items' },
    entry ? entry.itemDescriptionText : 'Catalog item',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !entry) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="ppmp-item-profile"
        header={
          <ProfileHeader
            id="ppmp-item-profile-header"
            icon={<ScrollText className="h-6 w-6" aria-hidden="true" />}
            // THE PLAIN TEXT, never the markup: a header is one line and a title carrying tags
            // would render them as text.
            title={entry.itemDescriptionText}
            meta={[
              `Counted in ${entry.unit}`,
              `${entry.lineItemCount} procurement ${entry.lineItemCount === 1 ? 'line' : 'lines'}`,
            ]}
            status={
              <StatusText tone={entry.isActive ? 'active' : 'inactive'}>
                {entry.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={entry.createdByName}
            createdAt={entry.createdAt}
            actions={
              <Menu
                id="ppmp-item-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Catalog item settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          entry.isActive ? (
            <ProfileBanner
              id="ppmp-item-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This catalog item is offered: a procurement line can draw its description, unit and cost from it in one pick."
            />
          ) : (
            <ProfileBanner
              id="ppmp-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This catalog item is deleted. It is no longer offered, and the lines already drawn from it keep the description they snapshotted."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="ppmp-item-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'ppmp-items.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Unit', value: entry.unit },
              {
                label: 'Estimated unit cost',
                // An em dash for unpriced — nobody has looked, which is not a cost of nothing.
                value:
                  entry.estimatedUnitCost == null
                    ? null
                    : formatMoney(entry.estimatedUnitCost),
                mono: true,
              },
              {
                label: 'Procurement lines',
                value: <span className="font-mono tabular-nums">{entry.lineItemCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/*
          A DOCUMENT GETS ITS OWN `padded` CARD, never a `<PreviewTable>` row: that table draws
          its own edges and caps its value column, and prose needs the inset and the width. The
          card shares the Edit of the section the field is collected on, which is why this one
          opens the same modal.
        */}
        <ProfileCard
          id="ppmp-item-description"
          title="Item description"
          padded
          menuItems={[
            {
              key: 'edit-description',
              label: 'Edit',
              permission: 'ppmp-items.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          {/*
            Sanitized on WRITE, never here. A second allow-list at the render site would be a
            weaker copy of the server's and the two would drift.
          */}
          <RichTextDocument html={entry.itemDescription} />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="ppmp-item-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: entry.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(entry.createdAt)} className="font-mono">
                    {formatDateTime(entry.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: entry.updatedByName },
              {
                label: 'Updated',
                value: entry.updatedAt ? (
                  <time dateTime={toIso(entry.updatedAt)} className="font-mono">
                    {formatDateTime(entry.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <PpmpItemModal
          open
          editing={entry}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="ppmp-items"
        entityTypes={['PpmpItemCatalog']}
        entityId={entry.id}
      />
    </div>
  );
}
